import { useEffect, useRef, useState } from "react";
import type { SettingsResponse } from "../../shared/types";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/Toast";
import { ApiError, api } from "../lib/api";
import { cleanLocal, looksLikeEmail } from "./rules";
import type { Run } from "./useSettingsData";

/** The first address replies go to: the team's list, or the person setting up when there is none. */
export function replyAddress(data: SettingsResponse, fallback: string): string {
  return (data.automations.reply_forward_to ?? "").split(",").map((x) => x.trim()).filter(Boolean)[0] ?? fallback;
}

const localOf = (data: SettingsResponse) => (data.sending.from_email ?? "hello@").split("@")[0] || "hello";

/**
 * The sender's name, the part before the @ and where replies go, with the three saves and the test email. Saves
 * queue behind each other, so a test sent right after an edit goes out with it.
 */
export function useSenderForm(data: SettingsResponse, run: Run, { fallbackName = "", said }: { fallbackName?: string; said?: string } = {}) {
  const { user } = useAuth();
  const toast = useToast();
  const s = data.sending;
  const savedReply = replyAddress(data, user?.email ?? "");
  const savedName = data.brand.from_name || fallbackName;
  const savedLocal = localOf(data);
  const [fromName, setFromName] = useState(savedName);
  const [local, setLocal] = useState(savedLocal);
  const [reply, setReply] = useState(savedReply);
  const [sending, setSending] = useState(false);
  useEffect(() => setFromName(savedName), [savedName]);
  useEffect(() => setLocal(savedLocal), [savedLocal]);
  useEffect(() => setReply(savedReply), [savedReply]);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const keep = (p: () => Promise<boolean>): Promise<boolean> => {
    const next = queue.current.then(p, p);
    queue.current = next;
    return next;
  };

  const nameChanged = !!fromName.trim() && fromName.trim() !== data.brand.from_name;
  const localChanged = !!s.domain && !!local && `${local}@${s.domain}` !== s.from_email;
  const replyChanged = reply.trim() !== savedReply;
  const dirty = nameChanged || localChanged || replyChanged;

  // Each save says whether it went through; one that was not needed has gone through.
  const saveName = (): Promise<boolean> =>
    nameChanged ? keep(() => run(() => api.settings.updateBrand({ from_name: fromName.trim() }), said)) : Promise.resolve(true);
  const saveLocal = (): Promise<boolean> => {
    const domain = s.domain;
    return domain && localChanged ? keep(() => run(() => api.settings.addSending({ domain, from_email: `${local}@${domain}` }), said)) : Promise.resolve(true);
  };
  /** False when what is typed is not an email address, after saying so. */
  const saveReply = (): Promise<boolean> => {
    const v = reply.trim();
    if (!replyChanged) return Promise.resolve(true);
    if (!looksLikeEmail(v)) {
      toast({ text: "That doesn’t look like an email address.", icon: "info" });
      return Promise.resolve(false);
    }
    const rest = (data.automations.reply_forward_to ?? "").split(",").map((x) => x.trim()).filter(Boolean).slice(1);
    return keep(() => run(() => api.settings.saveAutomations({ idle_minutes: data.automations.idle_minutes, reply_forward_to: [v, ...rest.filter((x) => x !== v)].join(", ") }), said));
  };
  /** Everything changed, in one go: nothing is saved while the reply address is wrong. True when all of it went through. */
  const saveAll = async (): Promise<boolean> => {
    if (replyChanged && !looksLikeEmail(reply.trim())) return saveReply();
    const done = await Promise.all([saveName(), saveLocal(), saveReply()]);
    return done.every(Boolean);
  };
  /** The test email once every save has landed; the address it went to, or null after saying why not. */
  const sendTest = async (): Promise<string | null> => {
    setSending(true);
    await queue.current;
    try {
      return (await api.settings.sendTest()).to;
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : "We couldn’t send the test just now.", icon: "info" });
      return null;
    } finally {
      setSending(false);
    }
  };

  return {
    fromName,
    local,
    reply,
    setFromName,
    setLocal: (v: string) => setLocal(cleanLocal(v)),
    setReply,
    dirty,
    sending,
    saveName,
    saveLocal,
    saveReply,
    saveAll,
    sendTest,
    discard: () => {
      setFromName(savedName);
      setLocal(savedLocal);
      setReply(savedReply);
    },
  };
}
